import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import parse from 'html-react-parser';
import useAnchor from 'js/hooks/useAnchor/useAnchor';
import Button from 'js/components/Common/Button/Button';
import {useLocation} from 'react-router-dom';
import useFetchData from 'js/hooks/useFetchData/useFetchData';
import Container from 'js/components/Common/Container/Container';
import Input from 'js/components/Common/FormComponents/Input/Input';
import useFetchCallback from 'js/hooks/useFetchCallback/useFetchCallback';
import SpinnerLoader from 'js/components/Common/SpinnerLoader/SpinnerLoader';
import useSearchReset from 'js/hooks/useSearchReset/useSearchReset';
import MixPanelWrapper from 'js/servises/MixPanelWrapper';
import {observer} from 'mobx-react';
import SearchStore from 'js/store/SearchStore';
import SearchService from 'js/containers/Pages/Search/SearchService';

import {useHistory} from 'react-router';
import classes from './SearchForm.module.pcss';
import AutoComplete from '../AutoComplete/AutoComplete';
import {incrementFetch, subtitleFetch} from './fetchData';
import AISearchToggle from '../AISearchToggle/AISearchToggle';


const SearchForm = ({headerRef, isHome = false}) => {
    const history = useHistory();
    const {
        isAISearch,
        searchQuery,
        showSearch,
        autoCompleteSpinnerStatus,
        lang
    } = SearchStore;
    const langMatch = useMemo(() => {
        switch (lang) {
            case 'en' : return 'Search';
            case '/en/et' : return 'Otsi';
            case '/en/es' : return 'Buscar';
            case '/en/it' : return 'Ricerca';
            case '/en/nl' : return 'Zoekopdracht';
            case '/en/de' : return 'Suchen';
            case '/en/pl' : return 'Szukaj';
            case '/en/pt' : return 'Procurar';
            case '/en/pt-PT' : return 'Pesquisa';
            case '/en/fr' : return 'Recherche';
            case '/en/cs' : return 'Vyhledávání';
            default : return 'Search';
        }
    }, [lang]);

    const location = useLocation();
    const inputRef = useRef(null);
    const autoCompleteRef = useRef(null);
    const resetSearch = useSearchReset({
        nodeRef: headerRef,
        isLocalRef: false
    });
    const [typingTimeOut, setTypingTimeout] = useState<any>(0);
    const [searchCountId, setSearchCountId] = useState<number>(0);
    const [searchSubtitle, setSearchSubtitle] = useState<string>('');
    const [autoCompleteQuery, setAutoCompleteQuery] = useState<string>('');
    const anchor = useAnchor('SearchResults', 0, -120);
    const [focusAutocompleteItem, setFocusAutocompleteItem] = useState<number | null>(-1);

    useEffect(() => {
        if (showSearch) {
            inputRef.current.focus();
        }
    }, [showSearch]);

   useEffect(() => {
       if (location.pathname !== '/search') {
           inputRef.current.value = '';
           SearchStore.setShowSearch(false);
           SearchStore.setFilterEntitiesData('');
           SearchStore.setFilterTaxData({
               suitableFor: '',
               skills: '',
               productLegacy: '',
               subjectMatter: '',
               language: '',
               articleType: '',
               courseType: '',
               eventType: '',
               videoType: '',
               podcastType: '',
               presentationType: '',
               manualsType: ''
           });
           SearchStore.setSearchStatus(false);
       }
   }, [
       location,
       searchQuery
   ]);

    useEffect(() => {
        const handlePopstate = (e) => {
            if (!e?.state?.state && e.currentTarget?.location?.pathname === '/search') {
                SearchStore.setSearchStatus(false);
                window.location.reload();
            }
        };

        window.addEventListener('popstate', handlePopstate);

        return () => {
            window.removeEventListener('popstate', handlePopstate);
        };
    }, []);

    const incrementSearchCount = useFetchCallback({
        updateData: () => {},
        query: e => `${incrementFetch(searchCountId, e)}`,
        onError: (e) => {
            console.error(e);
        }
    });

    useFetchData({
        query: subtitleFetch,
        condition: true,
        updateData: (data) => {
            setSearchSubtitle(data?.themeGeneralSettings?.Options?.searchFormSubtitle);
        }
    });

    const sendMixPanelEvent = useCallback(async (acValue:string) => {
        const wordsCount = SearchService.calculateWords(acValue);
        if (!acValue?.length) {
            return;
        }
        await MixPanelWrapper.TrackEvent(
            'Submit the search',
            {
                category: 'Search Input',
                value: acValue,
                words: wordsCount,
                characters: acValue.length,
                searchType: isAISearch ? 'AI' : 'Regular'
            });
    }, [isAISearch]);

    const searchCallback = useCallback( () => {
        incrementSearchCount(inputRef?.current.value);
        setSearchCountId(0);
        setAutoCompleteQuery('');
        SearchStore.setSearchQuery(inputRef?.current.value);
        SearchStore.setSearchPage(1);
        if (location.pathname !== '/search' && inputRef?.current?.value) {
            history?.push('/search');
        }
        if (location.pathname === '/search') {
            anchor();
        }
    }, [incrementSearchCount, location, history, anchor]);


    const searchSubmitHandler = useCallback(async (e) => {
        e.preventDefault();
        resetSearch();
        SearchStore.setAtoCompleteSpinnerStatus(false);
        SearchStore.setShowSearch(false);
        const inputValue = e?.target?.children[0]?.children[0]?.value;
        if (!inputValue) {
            SearchStore.setEntitiesCounts([]);
            SearchStore.setTaxCounts(
                {
                    suitableFor: [],
                    subjectMatter: [],
                    skills: [],
                    product: [],
                    language: [],
                    articleType: [],
                    courseType: [],
                    eventType: [],
                    videoType: [],
                    podcastType: [],
                    presentationType: [],
                    manualsType: []
                }
            );
            return;
        }
        searchCallback();
        await sendMixPanelEvent(inputRef?.current?.value);
        SearchStore.setSearchStatus(false);
        if (typingTimeOut) {
            clearTimeout(typingTimeOut);
        }
    }, [
        sendMixPanelEvent,
        resetSearch,
        typingTimeOut,
        searchCallback
    ]);

    const searchInputChangeHandler = useCallback((e) => {
        e.persist();
        if (typingTimeOut) {
            SearchStore.setAtoCompleteSpinnerStatus(false);
            clearTimeout(typingTimeOut);
        }
       setTypingTimeout(
           setTimeout( () =>  {
               if (!e?.target?.value?.length) {
                   setAutoCompleteQuery('');
                   SearchStore.setAtoCompleteSpinnerStatus(false);
                   setFocusAutocompleteItem(-1);
                   return;
               }
               SearchStore.setAtoCompleteSpinnerStatus(true);
               const filteredValue = e.target.value.replace(/\W/g, '');
               if (filteredValue) {
                   setAutoCompleteQuery(e.target.value);
                   setFocusAutocompleteItem(-1);
                   return;
               }
               SearchStore.setAtoCompleteSpinnerStatus(false);
               setAutoCompleteQuery('');
               setFocusAutocompleteItem(-1);
           }, 100)
       );
    }, [typingTimeOut]);

    const autoCompleteClickHandler = useCallback(async (e, id) => {
        resetSearch();
        inputRef.current.value = e?.currentTarget?.textContent || '';
        searchCallback();
        setSearchCountId(id);
        SearchStore.setTaxCounts(
            {
                suitableFor: [],
                skills: [],
                productLegacy: [],
                subjectMatter: [],
                language: [],
                articleType: [],
                courseType: [],
                eventType: [],
                videoType: [],
                podcastType: [],
                presentationType: [],
                manualsType: []
            }
        );
        SearchStore.setEntitiesCounts([]);
        SearchStore.setAtoCompleteSpinnerStatus(false);
        await sendMixPanelEvent(inputRef?.current?.value);
        if (typingTimeOut) {
            clearTimeout(typingTimeOut);
        }
    }, [
        resetSearch,
        sendMixPanelEvent,
        typingTimeOut,
        searchCallback
    ]);

    const autoCompleteChangeHandler = useCallback((e, id) => {
        if (focusAutocompleteItem >= 0) {
            setSearchCountId(id);
            inputRef.current.value = e?.textContent;
            inputRef.current.focus();
        }
    }, [focusAutocompleteItem]);

    const keyUpHandler = useCallback((e) => {
        e.persist();

        if (!autoCompleteRef?.current) {
            return;
        }
        const autoCompleteItemsIndexCount = autoCompleteRef.current.children[1].childElementCount - 1;
        const {key} = e || {};
        if (key === 'ArrowDown') {
            if (focusAutocompleteItem === autoCompleteItemsIndexCount) {
                setFocusAutocompleteItem(0);
            } else if (focusAutocompleteItem < autoCompleteItemsIndexCount) {
                setFocusAutocompleteItem(focusAutocompleteItem + 1);
            }
        }
        if (key === 'ArrowUp') {
            if (focusAutocompleteItem === -1 || focusAutocompleteItem === 0) {
                setFocusAutocompleteItem(autoCompleteItemsIndexCount);
            } else if (focusAutocompleteItem > 0 && focusAutocompleteItem < 25) {
                setFocusAutocompleteItem(focusAutocompleteItem - 1);
            }
        }
    }, [focusAutocompleteItem]);

    useEffect(() => {
        if (!autoCompleteRef?.current) {
            return;
        }
        const activeElement = autoCompleteRef.current.children[1].children[focusAutocompleteItem];
        if (activeElement) {
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [focusAutocompleteItem]);

    const closeAutoComplete = useCallback((e) => {
        if (!e.target.closest('[data-search]')) {
            SearchStore.setAtoCompleteSpinnerStatus(false);
            setAutoCompleteQuery('');
            if (typingTimeOut) {
                clearTimeout(typingTimeOut);
            }
        }

    }, [typingTimeOut]);

    const closeSearch = useCallback((e) => {
        if (!e.target.closest('[data-show-search-form]')) {
            SearchStore.setAtoCompleteSpinnerStatus(false);
            SearchStore.setShowSearch(false);
            setAutoCompleteQuery('');
            if (typingTimeOut) {
                clearTimeout(typingTimeOut);
            }
        }
    }, [typingTimeOut]);

    useEffect(() => {
        window.addEventListener('click', closeAutoComplete);
        window.addEventListener('click', closeSearch);
        return () => {
            window.removeEventListener('click', closeAutoComplete);
            window.removeEventListener('click', closeSearch);
        };
    }, [closeAutoComplete, closeSearch]);

    const spinnerStyles = useMemo(() => {
        if (isHome) {
            return {
                width: '25px',
                right: '215px',
                top: '11px'
            };
        }
        return {
            width: '35px',
            right: '190px',
            top: '23px'
        };
    }, [isHome]);

    return (
        <div data-show-search-form={true} className={`${classes.SearchForm} ${showSearch ? classes.Active : ''} ${isHome ? classes.Home : ''}`}>
            <Container additionalClass={classes.SearchFormWrap}>
                <form data-search={true} className={classes.SearchFormInner} onSubmit={searchSubmitHandler}>
                    <Input
                        type="text"
                        onKeyUp={e => keyUpHandler(e)}
                        addRef={inputRef}
                        onChange={searchInputChangeHandler}
                        name={'globalSearch'}
                        id={'globalSearch'}
                        minLength={1}
                        placeholder={langMatch}
                        autoComplete={'off'}
                        translate={false}
                        customStylesClass={classes.SearchInputField}
                    />
                    {isHome ?
                        <svg width="38" height="37" viewBox="0 0 38 37" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M17.8783 29.2917C24.6898 29.2917 30.2116 23.7698 30.2116 16.9583C30.2116 10.1468 24.6898 4.625 17.8783 4.625C11.0667 4.625 5.54492 10.1468 5.54492 16.9583C5.54492 23.7698 11.0667 29.2917 17.8783 29.2917Z"
                                stroke="#5C9AFC" strokeWidth="2" strokeLinecap="round"
                                strokeLinejoin="round"/>
                            <path d="M33.2941 32.3789L29.941 29.0239L26.5879 25.6689"
                                  stroke="#5C9AFC" strokeWidth="2" strokeLinecap="round"
                                  strokeLinejoin="round"/>
                        </svg> : ''}
                    <AISearchToggle />
                    <SpinnerLoader active={autoCompleteSpinnerStatus} additionalStyles={spinnerStyles}/>
                    <Button
                        type={'input'}
                        text={langMatch}
                        translate={false}
                        customStylesClass={classes.SearchButton}/>
                    <div className={classes.Subtitle}>{parse(searchSubtitle || '')}</div>
                    <AutoComplete
                        isHome={isHome}
                        itemRef={autoCompleteRef}
                        query={autoCompleteQuery}
                        focus={focusAutocompleteItem}
                        itemClick={autoCompleteClickHandler}
                        itemChange={autoCompleteChangeHandler}
                    />
                </form>
                <span className={classes.Close} onClick={() => SearchStore.setShowSearch(false)} />
            </Container>
        </div>
    );
};

export default observer(SearchForm);
